//@ flow
import { take, takeLatest, put, fork, select } from "redux-saga/effects";
import {
  alerta,
  reducer,
  isAbrindoArquivo,
  isRestaurandoConfiguracoes,
  adicionarMedidas,
  getMedidasSelecionadas,
  getEscalaMoedas,
  i18n,
} from "../state";
import { MODO_EXIBICAO_MEDIDA_COMPOSTA } from "../model";
import { identity, zipWith } from "ramda";

import { openModal } from "@quantum/ui";
type DescricaoLabel = {
  first: string,
  second: ?string,
};

function* adicionarMedidaComDescricaoSaga({ api }) {
  const geraEscalaNaDescricao = (translate, escalasMoedas, medida) => {
    const escala = medida.escala;
    const modoExibicao = medida.medida.modoExibicao;
    return modoExibicao === MODO_EXIBICAO_MEDIDA_COMPOSTA ||
      typeof escala === "undefined" ||
      escala === null ||
      escala === 0
      ? ""
      : " " +
          (translate("preposicaoEm") || "preposicaoEm") +
          " " +
          translate(escalasMoedas[escala].chaveTraducao);
  };

  yield takeLatest("ADICIONAR_MEDIDA_COM_DESCRICAO", function*(action): any {
    const { translate } = yield select(i18n);
    const escalasMoedas = yield select(getEscalaMoedas);

    try {
      const novasMedidasSelecionadas = getMedidasSelecionadas(
        reducer(
          yield select(identity),
          adicionarMedidas(
            action.medidasSelecionadas,
            false,
            action.posicao,
            action.novaMedida
          )
        )
      );

      if (action.novaMedida) {
        //Seta como "" as medidas com alias repetido
        const alias = action.medidasSelecionadas[0].alias;
        const medidasComAlias = [];
        novasMedidasSelecionadas.forEach(medida => {
          if (medida.alias === alias) {
            medidasComAlias.push(medida);
          }
        });

        medidasComAlias.forEach((medida, index) => {
          if (medida.alias === alias && index !== 0) {
            medida.alias = "";
          }
        });
      }

      const descricaoMedidasSelecionadas: Array<DescricaoLabel> = yield api.obterDescricaoMedidaSelecionada(
        novasMedidasSelecionadas
      );

      const medidasSelecionadas = zipWith(
        (medida, { first, second }) => ({
          ...medida,
          descricaoSelecao:
            first + geraEscalaNaDescricao(translate, escalasMoedas, medida),
          label: second,
        }),
        novasMedidasSelecionadas,
        descricaoMedidasSelecionadas
      );

      yield put(adicionarMedidas(medidasSelecionadas, true));
    } catch {
      yield put(openModal("erro-adicionar-medida"));
    }
  });

  yield takeLatest("RENOMEAR_MEDIDA_SELECIONADA", function*(action) {
    const { translate } = yield select(i18n);
    const escalasMoedas = yield select(getEscalaMoedas);

    try {
      const novasMedidasSelecionadas = yield select(getMedidasSelecionadas);

      const descricaoMedidasSelecionadas: Array<DescricaoLabel> = yield api.obterDescricaoMedidaSelecionada(
        novasMedidasSelecionadas
      );

      const medidasSelecionadas = zipWith(
        (medida, { first, second }) => ({
          ...medida,
          descricaoSelecao:
            first + geraEscalaNaDescricao(translate, escalasMoedas, medida),
          label: second,
        }),
        novasMedidasSelecionadas,
        descricaoMedidasSelecionadas
      );

      yield put(adicionarMedidas(medidasSelecionadas, true));
    } catch {
      yield put(openModal("erro-adicionar-medida"));
    }
  });

  yield takeLatest(
    action =>
      (action.type === "EXCLUIR_MEDIDA_SELECIONADA" && action.remocaoLogica) ||
      (action.type === "EXCLUIR_MEDIDAS_MARCADAS" && action.remocaoLogica),
    function*(action): any {
      const { translate } = yield select(i18n);
      const escalasMoedas = yield select(getEscalaMoedas);

      try {
        const novasMedidasSelecionadas = getMedidasSelecionadas(
          reducer(yield select(identity), { ...action, remocaoLogica: false })
        );

        const descricaoMedidasSelecionadas: Array<DescricaoLabel> = yield api.obterDescricaoMedidaSelecionada(
          novasMedidasSelecionadas
        );

        const medidasSelecionadas = zipWith(
          (medida, { first, second }) => ({
            ...medida,
            descricaoSelecao:
              first + geraEscalaNaDescricao(translate, escalasMoedas, medida),
            label: second,
          }),
          novasMedidasSelecionadas,
          descricaoMedidasSelecionadas
        );

        yield put(adicionarMedidas(medidasSelecionadas, true));
      } catch {
        // TODO: i18n
        yield put(
          alerta(
            action.type === "EXCLUIR_MEDIDA_SELECIONADA"
              ? "Essa medida faz parte de uma medida personalizada, portanto não pode ser retirada."
              : "Existem medidas que fazem parte de uma medida personalizada, e por isso não podem ser retiradas."
          )
        );
      }
    }
  );

  yield takeLatest(
    ["SELECIONAR_DATA_BASE", "SELECIONAR_DATA_BASE_DEFAULT"],
    function*(action): any {
      const escalasMoedas = yield select(getEscalaMoedas);

      const restaurandoConfiguracoes = yield select(isRestaurandoConfiguracoes);
      if (restaurandoConfiguracoes) {
        return;
      }

      const abrindoArquivo = yield select(isAbrindoArquivo);

      if (abrindoArquivo) {
        yield take("ABRIR_ARQUIVO_SUCCESS");
      }

      const medidasSelecionadasState: Array<MedidaSelecionada> = yield select(
        getMedidasSelecionadas
      );

      const descricaoMedidasSelecionadas: Array<DescricaoLabel> = yield api
        .obterDescricaoMedidaSelecionada(medidasSelecionadasState)
        .catch(() =>
          medidasSelecionadasState.map(medida => medida.descricaoSelecao)
        );

      const { translate } = yield select(i18n);
      const medidasSelecionadas = zipWith(
        (medida, { first, second }) => ({
          ...medida,
          descricaoSelecao:
            first + geraEscalaNaDescricao(translate, escalasMoedas, medida),
          label: second,
        }),
        medidasSelecionadasState,
        descricaoMedidasSelecionadas
      );
      yield put(adicionarMedidas(medidasSelecionadas, true));
    }
  );
}

export default function* descricaoMedidasSaga({ api }) {
  yield fork(adicionarMedidaComDescricaoSaga, { api });
}
